import { useState, useCallback } from 'react';
import { AlertTriangle, Copy, RefreshCw, RotateCcw } from 'lucide-react';
import { Button } from '@/modules/shared/components/ui/button';
import { formatErrorForClipboard, fallbackCopyTextToClipboard } from '@/modules/shared/utils/formatErrorForClipboard';

/**
 * Severity determines which variant of the fallback UI to render:
 * - `page`   – full-height centered card (for page-level or root boundaries)
 * - `section` – compact inline card (for component-level boundaries inside a page)
 */
export type ErrorFallbackSeverity = 'page' | 'section';

interface ErrorFallbackProps {
  error?: Error | null;
  onRetry?: () => void;
  severity?: ErrorFallbackSeverity;
  /** Component stack from ErrorBoundary's componentDidCatch */
  componentStack?: string | null;
}

const isDev = import.meta.env.DEV;

export function ErrorFallback({ error, onRetry, severity = 'page', componentStack }: ErrorFallbackProps) {
  const [copied, setCopied] = useState(false);
  const handleReload = () => window.location.reload();

  const handleCopyError = useCallback(async () => {
    const errorText = formatErrorForClipboard({
      error,
      componentStack,
    });

    try {
      await navigator.clipboard.writeText(errorText);
    } catch {
      fallbackCopyTextToClipboard(errorText);
    }

    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  }, [error, componentStack]);

  if (severity === 'section') {
    return (
      <div className="flex items-center justify-center min-h-[120px] w-full px-4 py-3">
        <div className="bg-surface-premium border border-border-hairline rounded-lg p-4 text-center space-y-2 max-w-md w-full">
          <div className="flex items-center justify-center gap-1.5 text-warning">
            <AlertTriangle className="w-3.5 h-3.5" />
            <span className="text-xs font-medium">Failed to load this section</span>
          </div>
          {isDev && error?.message && (
            <p className="text-[11px] text-muted-foreground break-words line-clamp-2">
              {error.message}
            </p>
          )}
          {onRetry && (
            <Button variant="outline" size="sm" onClick={onRetry} className="h-7 text-[11px] gap-1">
              <RotateCcw className="w-3 h-3" />
              Retry
            </Button>
          )}
        </div>
      </div>
    );
  }

  // severity === 'page'
  return (
    <div className="flex items-center justify-center min-h-[400px] w-full px-4">
      <div className="bg-surface-premium border border-border-hairline rounded-xl p-6 text-center space-y-4 max-w-lg w-full shadow-sm">
        <div className="mx-auto w-10 h-10 rounded-full bg-destructive-soft flex items-center justify-center">
          <AlertTriangle className="w-3.5 h-3.5 text-destructive" />
        </div>

        <div className="space-y-1">
          <h2 className="text-sm font-semibold text-foreground">Something went wrong</h2>
          <p className="text-xs text-muted-foreground">
            An unexpected error occurred. Please try again or refresh the page.
          </p>
        </div>

        {isDev && error?.message && (
          <div className="rounded-md border border-destructive/20 bg-muted p-3 text-left">
            <p className="text-[11px] font-medium text-destructive break-words">{error.message}</p>
            {error.stack && (
              <pre className="text-[11px] text-muted-foreground overflow-auto max-h-32 whitespace-pre-wrap mt-1">
                {error.stack}
              </pre>
            )}
          </div>
        )}

        <div className="flex gap-2 justify-center flex-wrap">
          <Button variant="outline" size="sm" onClick={handleCopyError} className="gap-1">
            <Copy className="w-3 h-3" />
            {copied ? 'Copied' : 'Copy Error'}
          </Button>
          {onRetry && (
            <Button variant="outline" size="sm" onClick={onRetry} className="gap-1">
              <RotateCcw className="w-3 h-3" />
              Try Again
            </Button>
          )}
          <Button size="sm" onClick={handleReload} className="gap-1">
            <RefreshCw className="w-3 h-3" />
            Reload Page
          </Button>
        </div>
      </div>
    </div>
  );
}
